/**
 * CostDashboard — B4: Admin Cost Dashboard
 * SVG bar charts, per-user spend table (sortable), daily projection.
 */
import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface UserCostRow {
  user_id: string;
  email: string;
  name: string | null;
  total_cost: number;
  today_cost: number;
  query_count: number;
}

interface DailyPoint {
  date: string;
  spend: number;
  queries: number;
}

interface CostData {
  per_user: UserCostRow[];
  daily_chart: DailyPoint[];
  today_spend: number;
  today_queries: number;
  projected_daily: number;
}

type SortKey = 'total_cost' | 'today_cost' | 'query_count' | 'email';
type SortDir = 'asc' | 'desc';

function formatUsd(v: number): string {
  return `$${v.toFixed(2)}`;
}

function BarChart({ data, height = 80 }: { data: DailyPoint[]; height?: number }) {
  if (!data.length) return null;
  const maxSpend = Math.max(...data.map(d => d.spend), 0.0001);
  const barW = 100 / data.length;

  return (
    <div className="cd-chart-wrap" aria-label="Daily spend chart">
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="cd-chart-svg"
        role="img"
        aria-label="30-day spend bar chart"
      >
        {data.map((d, i) => {
          const barH = maxSpend > 0 ? (d.spend / maxSpend) * (height - 10) : 0;
          const x = i * barW;
          const y = height - barH;
          const isToday = i === data.length - 1;
          return (
            <g key={d.date}>
              <rect
                x={x + barW * 0.1}
                y={y}
                width={barW * 0.8}
                height={barH}
                fill={isToday ? 'var(--accent)' : 'var(--accent-soft, rgba(99,102,241,0.3))'}
                rx="1"
              />
              <title>{`${d.date}: ${formatUsd(d.spend)} (${d.queries} queries)`}</title>
            </g>
          );
        })}
        {/* Zero line */}
        <line x1="0" y1={height - 0.5} x2="100" y2={height - 0.5} stroke="var(--border, #334)" strokeWidth="0.5" />
      </svg>
      <div className="cd-chart-labels">
        <span>{data[0]?.date?.slice(5) ?? ''}</span>
        <span>{data[Math.floor(data.length / 2)]?.date?.slice(5) ?? ''}</span>
        <span>{data[data.length - 1]?.date?.slice(5) ?? ''}</span>
      </div>
    </div>
  );
}

interface CostDashboardProps {
  isVisible: boolean;
}

export default function CostDashboard({ isVisible }: CostDashboardProps) {
  const [data, setData] = useState<CostData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('total_cost');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/costs`);
      if (!res.ok) throw new Error(`Cost fetch failed (${res.status})`);
      const json = await res.json() as CostData;
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isVisible) void load();
  }, [isVisible, load]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedUsers = data
    ? [...data.per_user].sort((a, b) => {
        const mul = sortDir === 'desc' ? -1 : 1;
        if (sortKey === 'email') return mul * (a.email ?? '').localeCompare(b.email ?? '');
        return mul * ((a[sortKey] as number) - (b[sortKey] as number));
      })
    : [];

  const th = (key: SortKey, label: string) => (
    <th
      className={`cd-th cd-th--sortable${sortKey === key ? ' cd-th--active' : ''}`}
      onClick={() => handleSort(key)}
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleSort(key)}
      aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      {label} {sortKey === key ? (sortDir === 'desc' ? '↓' : '↑') : ''}
    </th>
  );

  if (!isVisible) return null;

  return (
    <div className="cd-root">
      <div className="cd-section-title">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
        Cost Dashboard
        <button className="cd-refresh-btn" onClick={() => void load()} title="Refresh" aria-label="Refresh cost data" style={{ marginLeft: 'auto' }}>
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
        </button>
      </div>

      {loading && <div className="cd-loading">Loading cost data…</div>}
      {error && <div className="cd-error">{error}</div>}

      {data && (
        <>
          {/* Summary cards */}
          <div className="cd-summary-row">
            <div className="cd-summary-card">
              <div className="cd-summary-label">Today's spend</div>
              <div className="cd-summary-value">{formatUsd(data.today_spend)}</div>
            </div>
            <div className="cd-summary-card">
              <div className="cd-summary-label">Today's queries</div>
              <div className="cd-summary-value">{data.today_queries}</div>
            </div>
            <div className="cd-summary-card">
              <div className="cd-summary-label">Projected (24h)</div>
              <div className="cd-summary-value">{formatUsd(data.projected_daily)}</div>
            </div>
          </div>

          {/* 30-day chart */}
          <div className="cd-chart-section">
            <div className="cd-chart-title">30-day spend</div>
            <BarChart data={data.daily_chart} height={80} />
          </div>

          {/* Per-user spend table removed — aggregate spend shown above */}
        </>
      )}
    </div>
  );
}
